import { useState, useRef, useEffect, useCallback, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";

interface UserMenuProps {
  /** Optional extra section rendered above the sign-out actions (e.g. a layout picker). */
  extraSection?: ReactNode;
}

export function UserMenu({ extraSection }: UserMenuProps = {}) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ESC key to close and restore focus
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        className="flex h-9 min-w-[4.5rem] items-center justify-center rounded-full px-4 text-sm font-medium transition-all duration-200 bg-surface2 text-foreground border border-border/30 hover:bg-[hsl(var(--forest-green-glow))] hover:text-accent hover:border-accent/20"
      >
        {auth.user?.full_name?.split(" ")[0] || "Menu"}
      </button>

      {open && (
        <div role="menu" className="absolute right-0 top-full mt-2 w-48 rounded-lg bg-surface py-1 shadow-soft">
          <Link
            to="/me"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-foreground hover:bg-surface2"
          >
            My Profile
          </Link>
          {(auth.user?.role === "admin" || auth.permissions?.can_manage_settings === true) && (
            <Link
              to="/settings"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-foreground hover:bg-surface2"
            >
              Settings
            </Link>
          )}
          {extraSection && (
            <>
              <hr className="my-1" />
              {extraSection}
            </>
          )}
          <hr className="my-1" />
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void auth.signOut();
            }}
            className="block w-full px-4 py-2 text-left text-sm text-foreground hover:bg-surface2"
          >
            Sign out
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void auth.signOutAndForgetDevice();
            }}
            className="block w-full px-4 py-2 text-left text-sm text-muted hover:bg-surface2"
            title="Sign out and require 2FA verification on next login"
          >
            Sign out & forget device
          </button>
        </div>
      )}
    </div>
  );
}
